<template>
  <b-container>
    <b-alert
      variant="warning"
      :show="onHold"
      class="p-2 mb-0">
      <b-row>
        <b-col
          cols="8"
          class="pt-2">
          Config placed on hold by {{ heldBy }}
        </b-col>
        <b-col>
          <b-btn
            variant="warning"
            class="float-right"
            @click="clearHold">
            Clear Hold
          </b-btn>
        </b-col>
      </b-row>
    </b-alert>
    <b-alert
      variant="info"
      :show="onSchedule"
      class="p-2 mb-0">
      <b-row>
        <b-col
          cols="8"
          class="pt-2">
          Promotion is scheduled for <strong>{{ formatDateTime(scheduledTime) }} </strong> by {{ scheduledBy }}
        </b-col>
        <b-col>
          <b-btn
            variant="warning"
            class="float-right"
            @click="cancelSchedule">
            Cancel
          </b-btn>
        </b-col>
      </b-row>
    </b-alert>
    <tool-header
      :tool-name="toolName"
      :tool-icon-text="toolIconText"
      class="mb-3">
      <slot name="header" />
    </tool-header>
    <b-row
      v-if="canEdit"
      class="mt-2">
      <b-col id="beta-publish-button-wrapper">
        <b-btn
          :disabled="!enableBetaPublish"
          variant="primary"
          class="w-100"
          @click="onPublishToBeta">
          Publish to {{ betaEnvName }}
        </b-btn>
        <b-tooltip
          v-if="onHold"
          :disabled="enableBetaPublish"
          target="beta-publish-button-wrapper"
          title="Config is on hold. Contact the person who put the config on hold to clear the hold if you need to make changes." />
        <b-tooltip
          v-else
          :disabled="enableBetaPublish"
          target="beta-publish-button-wrapper"
          title="No changes have yet been made. Please make at least one change to enable" />
      </b-col>
      <b-col id="prod-publish-button-wrapper">
        <b-btn
          v-if="canPublish"
          :disabled="!enableProductionPublish || enableBetaPublish"
          variant="primary"
          class="w-100"
          @click.prevent="showPublishModal = true">
          Publish to {{ liveEnvName }}
        </b-btn>
        <b-tooltip
          v-if="enableBetaPublish"
          target="prod-publish-button-wrapper"
          :title="'Please publish unsaved changes to ' + betaEnvName + ' first to enable'" />
        <b-tooltip
          v-else-if="onHold"
          target="prod-publish-button-wrapper"
          title="Config is currently on hold, please remove hold first to enable publishing." />
        <b-tooltip
          v-else-if="onSchedule"
          target="prod-publish-button-wrapper"
          title="Config is currently scheduled to be promoted." />
        <b-tooltip
          v-else-if="!enableProductionPublish"
          target="prod-publish-button-wrapper"
          :title="'No unpublished changes currently exist in ' + betaEnvName + '. Publish changes first to enable'" />
      </b-col>
      <b-col
        class="text-right"
        cols="1">
        <b-dropdown
          variant="outline-primary"
          :disabled="!enableProductionPublish"
          no-caret>
          <template v-slot:button-content>
            <i class="fa fa-bars" />
          </template>
          <b-dropdown-item
            variant="dark"
            :disabled="!canEdit"
            @click="hold">
            Place Hold
          </b-dropdown-item>
          <b-dropdown-item
            v-b-modal.schedule-modal
            :disabled="!canPublish"
            variant="dark">
            Schedule Promotion
          </b-dropdown-item>
        </b-dropdown>
      </b-col>
    </b-row>
    <config-links v-bind="configLinkConfig" />
    <slot :editable="editable" />
    <b-modal
      v-if="canPublish"
      v-model="showPublishModal"
      title="Review Staged Changes"
      size="xl"
      hide-footer>
      <json-diff-viewer
        :original-json="prodConfig"
        :changed-json="betaConfig"
        :show-unchanged="true" />
      <b-form
        class="mt-3"
        @submit.prevent="onPublishToProd"
        @reset="showPublishModal = false">
        <b-btn
          type="submit"
          variant="primary"
          class="ml-2 xl-wide float-right">
          Publish
        </b-btn>
        <b-btn
          type="reset"
          variant="outline-secondary"
          class="xl-wide float-right">
          Cancel
        </b-btn>
      </b-form>
    </b-modal>
    <b-modal
      id="schedule-modal"
      ref="scheduleModal"
      size="md"
      title="Schedule Banners Promotion"
      hide-footer
      hide-header
      no-close-on-backdrop>
      <config-schedule-request-form
        @on-submit="schedulePromotion"
        @cancel="$refs.scheduleModal.hide()" />
    </b-modal>
  </b-container>
</template>

<script>
  import ToolConfig from "@/services/api/ToolConfig"
  import JsonDiffViewer from "@/components/JsonDiffViewer"
  import {diff} from "deep-object-diff"
  import {mapGetters} from "vuex"
  import ToolHeader from "@/components/ToolHeader"
  import ConfigScheduleRequestForm from "@/components/Scheduling/ConfigScheduleRequestForm"
  import moment from 'moment'
  import ConfigLinks from "@/components/ConfigLinks"
  import {DateTime} from "luxon"

  export default {
    name: "ConfigPublisher",
    components: {
      ConfigLinks,
      ConfigScheduleRequestForm,
      ToolHeader,
      JsonDiffViewer,
    },
    props: {
      value: {
        type: Object,
        required: true,
      },
      configName: {
        type: String,
        required: true,
      },
      toolName: {
        type: String,
        required: true,
      },
      toolIconText: {
        type: String,
        default: "",
      },
      canEdit: {
        type: Boolean,
        default: false,
      },
      canPublish: {
        type: Boolean,
        default: false,
      },
      isFormValid: {
        type: Function,
        default: () => true,
      },
    },
    data: () => ({
      showPublishModal: false,
      onHold: false,
      heldBy: null,
      onSchedule: false,
      scheduledBy: null,
      scheduledTaskId: null,
      scheduledTime: null,
      betaConfig: null,
      prodConfig: null,
      configLinkConfig: {
        betaUrl: "",
        prodUrl: "",
        betaLastModifiedBy: "",
        betaLastModifiedOn: null,
        prodLastModifiedBy: "",
        prodLastModifiedOn: null,
      },
    }),
    computed: {
      editable() {
        return !this.onHold && !this.onSchedule
      },
      ...mapGetters([
        'betaEnvName',
        'liveEnvName',
      ]),
      enableBetaPublish() {
        if (!this.value || !this.betaConfig) {
          return false
        }
        const localDiff = diff(this.value, this.betaConfig)
        return Object.entries(localDiff).length !== 0
      },
      enableProductionPublish() {
        if (!this.betaConfig || !this.prodConfig) {
          return false
        }
        if (this.onHold || this.onSchedule) {
          return false
        }
        const betaProdDiff = diff(this.betaConfig, this.prodConfig)
        return Object.entries(betaProdDiff).length !== 0
      },
    },
    watch: {
      configName(val) {
        this.loadConfigs(val)
      },
    },
    async created() {
      await this.loadConfigs()
    },
    methods: {
      async loadConfigs(configName) {
        await this.$loading.showWhile(async () => {
          const config = configName ? configName : this.configName
          const configUrls = await ToolConfig.getConfigBaseUrls(config)
          const prodResponse = await ToolConfig.getConfig(configUrls.gcpLiveUrl, {cacheBust: true})
          const betaResponse = await ToolConfig.getConfig(configUrls.gcpBetaUrl, {cacheBust: true})

          if (betaResponse.headers["x-goog-meta-held-by"]) {
            this.heldBy = betaResponse.headers["x-goog-meta-held-by"]
            this.onHold = true
          } else {
            this.onHold = false
          }

          if (betaResponse.headers["x-goog-meta-cloudtaskname"]) {
            this.scheduledTaskId = betaResponse.headers["x-goog-meta-cloudtaskname"]
            this.onSchedule = true
            this.scheduledBy = betaResponse.headers["x-goog-meta-requestinguser"]
            this.scheduledTime = betaResponse.headers["x-goog-meta-promotetime"]
          } else {
            this.onSchedule = false
          }

          const configLinks = {
            betaUrl: configUrls.betaUrl,
            prodUrl: configUrls.liveUrl,
          }
          configLinks.betaLastModifiedOn = DateTime.fromHTTP(betaResponse.headers["last-modified"])
          if (betaResponse.headers["x-goog-meta-last-changed-by"]) {
            configLinks.betaLastModifiedBy = betaResponse.headers["x-goog-meta-last-changed-by"]
          }
          configLinks.prodLastModifiedOn = DateTime.fromHTTP(prodResponse.headers["last-modified"])
          if (prodResponse.headers["x-goog-meta-last-changed-by"]) {
            configLinks.prodLastModifiedBy = prodResponse.headers["x-goog-meta-last-changed-by"]
          }
          this.configLinkConfig = Object.assign(this.configLinkConfig, configLinks)

          this.betaConfig = betaResponse.data
          this.prodConfig = prodResponse.data

          // Copy the current beta config to the local version for editing
          this.$emit('input', JSON.parse(JSON.stringify(this.betaConfig)))
        })
      },
      async hold() {
        await ToolConfig.hold(this.configName)
        await this.loadConfigs()
      },
      async clearHold() {
        const modalConfirmed = await this.$confirm.open(`Are you sure you wish to clear the hold placed by ${this.heldBy}?`)
        if (!modalConfirmed) {
          return
        }
        await ToolConfig.release(this.configName)
        await this.loadConfigs()
      },
      async schedulePromotion(promotionDate) {
        this.$refs.scheduleModal.hide()

        await ToolConfig.schedulePromotion(this.configName, promotionDate)
        await this.loadConfigs()
      },
      async cancelSchedule() {
        const modalConfirmed = await this.$confirm.open(`Are you sure you wish to cancel the scheduled promotion placed by ${this.scheduledBy}?`)
        if (!modalConfirmed) {
          return
        }
        await ToolConfig.cancelScheduledPromotion(this.configName, this.scheduledTaskId)
        await this.loadConfigs()
      },
      async onPublishToBeta() {
        if(!this.isFormValid()) {
          this.$alert.warning("Config contains one or more errors. Please fix these errors before publishing.")
          return
        }

        const modalConfirmed = await this.$confirm.open(`Are you sure you wish to publish all changes to the ${this.betaEnvName} environment?`)
        if (!modalConfirmed) {
          return
        }

        await ToolConfig.updateConfig(this.configName, this.value)
        this.$alert.success(`Configuration successfully published to the ${this.betaEnvName} environment`)
        this.componentKey += 1
        await this.loadConfigs()
      },
      async onPublishToProd() {
        
        if (!this.enableProductionPublish) {
          return
        }

        this.showPublishModal = false
        await ToolConfig.publishToProduction(this.configName)
        this.$alert.success(`Configuration published successfully to ${this.liveEnvName}`)
        await this.loadConfigs()
      },
      formatDateTime(date) {
        return moment(date).tz(moment.tz.guess()).format('MMM DD hh:mm A z')
      },
      configHasChanged() {
        return this.enableBetaPublish
      },
    },
  }
</script>
<style>
  .tooltip .tooltip-inner{
    max-width: 350px !important;
    width: 300px !important;
  }
</style>
